import { useCallback, useEffect, useRef, useState } from "react";
import { useArenaStore } from "./stores/useArenaStore";
import { createBattlefieldScene, type SceneHandle } from "./three/scene";
import {
  getSnapshot,
  joinArena,
  sendMove,
  sendShoot,
} from "./lib/network";
import { POLL_INTERVAL_MS } from "@shared/constants";
import { Lobby } from "./components/lobby/Lobby";
import { HUD } from "./components/hud/HUD";
import { UpgradeShop } from "./components/hud/UpgradeShop";
import { KillFeed } from "./components/hud/KillFeed";
import { ScoreBar } from "./components/hud/ScoreBar";
import { Crosshair } from "./components/hud/Crosshair";
import { RespawnOverlay } from "./components/hud/RespawnOverlay";
import { TutorialOverlay } from "./components/hud/TutorialOverlay";
import { DamageNumbers } from "./components/hud/DamageNumbers";
import { setSceneHandle } from "./lib/sceneRef";

export function App() {
  const store = useArenaStore();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<SceneHandle | null>(null);
  const keysRef = useRef<Set<string>>(new Set());
  const [inBattle, setInBattle] = useState(false);

  // ── scene lifecycle ──
  useEffect(() => {
    if (!inBattle || !canvasRef.current) return;
    const scene = createBattlefieldScene(canvasRef.current);
    sceneRef.current = scene;
    setSceneHandle(scene);
    return () => {
      scene.dispose();
      sceneRef.current = null;
      setSceneHandle(null);
    };
  }, [inBattle]);

  // ── input ──
  useEffect(() => {
    if (!inBattle) return;
    const down = (e: KeyboardEvent) => {
      keysRef.current.add(e.code);
      if (e.code === "KeyE") {
        useArenaStore.getState().setShopOpen(!useArenaStore.getState().shopOpen);
      }
    };
    const up = (e: KeyboardEvent) => keysRef.current.delete(e.code);
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, [inBattle]);

  // ── movement loop (client-side prediction, throttled send) ──
  // We keep a *local predicted position* that advances only while a movement
  // key is held. When idle we reconcile to the server position. This avoids the
  // old feedback loop where every frame re-added speed to the (stale) server
  // position, ballooning the delta until the server's per-request clamp made the
  // player appear frozen.
  useEffect(() => {
    if (!inBattle) return;
    let raf = 0;
    let lastSent = 0;
    let predX: number | null = null;
    let predZ: number | null = null;

    const loop = () => {
      raf = requestAnimationFrame(loop);
      const scene = sceneRef.current;
      const { playerId, state } = useArenaStore.getState();
      if (!scene || !playerId || !state) return;
      const me = state.players.find((p) => p.id === playerId);
      if (!me || me.isDead) return;

      const keys = keysRef.current;
      let dx = 0;
      let dz = 0;
      if (keys.has("KeyW") || keys.has("ArrowUp")) dz -= 1;
      if (keys.has("KeyS") || keys.has("ArrowDown")) dz += 1;
      if (keys.has("KeyA") || keys.has("ArrowLeft")) dx -= 1;
      if (keys.has("KeyD") || keys.has("ArrowRight")) dx += 1;

      if (dx === 0 && dz === 0) {
        // idle: hold the last predicted position as the render source. Do NOT
        // snap to the (stale) server position — the scene's soft correction
        // (setSelfCorrection) will ease the avatar to the true stop point over
        // the next ~100-200ms, which is what makes stopping feel smooth.
        if (predX !== null && predZ !== null) {
          scene.setDesiredPos(predX, predZ);
        }
        return;
      }

      // moving: advance local prediction from where we last were
      const len = Math.sqrt(dx * dx + dz * dz);
      const speed = 0.35;
      if (predX === null || predZ === null) {
        predX = me.pos.x;
        predZ = me.pos.z;
      }
      predX += (dx / len) * speed;
      predZ += (dz / len) * speed;
      scene.setDesiredPos(predX, predZ);

      const now = Date.now();
      if (now - lastSent > 100) {
        lastSent = now;
        sendMove(useArenaStore.getState().arenaId!, playerId, {
          x: predX,
          y: 0,
          z: predZ,
        }).catch(() => {});
      }
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [inBattle]);

  // ── shooting (click / space) ──
  useEffect(() => {
    if (!inBattle) return;
    const shoot = () => {
      const scene = sceneRef.current;
      const { arenaId, playerId } = useArenaStore.getState();
      if (!scene || !arenaId || !playerId) return;
      const target = scene.pickTarget();
      if (!target) return;
      sendShoot(arenaId, playerId, target.pos, target.targetId ?? undefined).catch(() => {});
    };
    const onMouseDown = (e: MouseEvent) => {
      if (e.button === 0) shoot();
    };
    const onSpace = (e: KeyboardEvent) => {
      if (e.code === "Space") {
        e.preventDefault();
        shoot();
      }
    };
    window.addEventListener("mousedown", onMouseDown);
    window.addEventListener("keydown", onSpace);
    return () => {
      window.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("keydown", onSpace);
    };
  }, [inBattle]);

  // ── polling: latency-aware. Chain the next request as soon as the previous
  // returns (so high-RTT production still polls back-to-back), but never faster
  // than POLL_INTERVAL_MS. Non-overlapping by construction. ──
  useEffect(() => {
    if (!inBattle || !store.arenaId) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let seen: Record<string, { x: number; z: number; hp: number }> = {};
    let lastTick = -1;

    const poll = async () => {
      if (stopped) return;
      const started = Date.now();
      try {
        const snap = await getSnapshot(store.arenaId!, lastTick, seen);
        const state = useArenaStore.getState().mergeSnapshot(snap);
        lastTick = state.tickCount;
        const nextSeen: Record<string, { x: number; z: number; hp: number }> = {};
        for (const p of state.players) nextSeen[p.id] = { x: p.pos.x, z: p.pos.z, hp: p.hp };
        for (const c of state.creatures) nextSeen[c.id] = { x: c.pos.x, z: c.pos.z, hp: c.hp };
        seen = nextSeen;
        sceneRef.current?.applyState(state, useArenaStore.getState().playerId);
        sceneRef.current?.setSelf(useArenaStore.getState().playerId);
        // feed the server-authoritative self position as a soft correction so the
        // local prediction stays honest without visible rubber-banding
        const selfPlayer = state.players.find((p) => p.id === useArenaStore.getState().playerId);
        if (selfPlayer) {
          sceneRef.current?.setSelfCorrection(selfPlayer.pos.x, selfPlayer.pos.z);
        }
      } catch {
        // retry next tick
      }
      if (stopped) return;
      const elapsed = Date.now() - started;
      const wait = Math.max(0, POLL_INTERVAL_MS - elapsed);
      timer = setTimeout(poll, wait);
    };
    poll();

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [inBattle, store.arenaId]);

  // ── join handler ──
  const handleJoin = useCallback(async (arenaId: string, name: string, faction: "dawn" | "dusk") => {
    const res = await joinArena(arenaId, name, faction);
    useArenaStore.getState().setIdentity(arenaId, res.playerId, res.name, res.faction);
    useArenaStore.getState().setState(res.state);
    setInBattle(true);
  }, []);

  if (!inBattle) {
    return <Lobby onJoin={handleJoin} />;
  }

  return (
    <div className="relative h-full w-full">
      <canvas ref={canvasRef} className="h-full w-full" />
      <Crosshair />
      {store.state && <ScoreBar state={store.state} />}
      {store.state && <HUD state={store.state} />}
      {store.state && <KillFeed state={store.state} />}
      {store.state && <DamageNumbers state={store.state} />}
      {store.shopOpen && store.state && <UpgradeShop state={store.state} />}
      {store.state && <TutorialOverlay state={store.state} />}
      {store.state && <RespawnOverlay state={store.state} />}
    </div>
  );
}
